import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ScrollText, Landmark, Target } from "lucide-react";
import { PageHero, Overline } from "@/components/Bits";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { ParallaxImage } from "@/components/ParallaxImage";
import { BrandMarquee } from "@/components/BrandMarquee";
import { IMAGES, LEADERSHIP, LEGAL } from "@/data/content";

const VALUES = [
  { icon: Target, title: "Purpose", text: "We focus on work that creates real opportunities and makes a meaningful difference in people's lives." },
  { icon: ScrollText, title: "Integrity", text: "We lead with honesty, accountability, and care in how we use our resources and earn the trust placed in us." },
  { icon: Landmark, title: "Community", text: "We listen, learn, and work alongside the people and communities we serve." },
];

export default function About() {
  return (
    <>
      <PageHero
        label="About Us"
        title="Building brighter futures,"
        accent="together."
        subtitle="Thefrent Foundation is a nonprofit organization formed on August 10, 2026, with a mission to expand access and opportunity in education, athletics, health and wellness, leadership development, and innovation."
      />

      {/* Story */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-border">
              <div className="absolute inset-0 z-10 ring-1 ring-inset ring-gold/25" />
              <ParallaxImage src={IMAGES.classroom} alt="Students learning together" className="aspect-[4/3] w-full" priority />
            </div>
          </Reveal>
          <div>
            <Reveal><Overline>Our Story</Overline></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
                Opportunity should never depend on circumstance.
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-muted-foreground">
                <p>
                  Founded on a simple belief — that talent is universal but opportunity is not —
                  Thefrent Foundation exists to close that gap. Through scholarships, grants,
                  programs, and community partnerships, we empower individuals to reach their
                  fullest potential.
                </p>
                <p>
                  Our work spans five interconnected pillars, because a brighter future is never
                  built on one thing alone. It takes education and health, leadership and play,
                  curiosity and community — working together.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <BrandMarquee dark />

      {/* Values */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
        <Reveal><Overline>What Guides Us</Overline></Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-2xl font-heading text-3xl font-bold tracking-tight sm:text-4xl">
            Values that hold us <span className="italic text-gold">accountable.</span>
          </h2>
        </Reveal>
        <Stagger className="mt-14 grid gap-6 md:grid-cols-3">
          {VALUES.map((v) => (
            <StaggerItem key={v.title}>
              <div className="flair-card h-full rounded-2xl border border-border bg-card p-8">
                <span className="flair-icon grid h-12 w-12 place-items-center rounded-full bg-gold/12 text-gold ring-1 ring-gold/25">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-heading text-xl font-semibold">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{v.text}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* Full-bleed brand statement */}
      <section
        className="relative w-full overflow-hidden bg-gradient-to-r from-black via-[#0d1d47] to-black py-20 md:py-28"
        data-testid="on-purpose-band"
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <motion.p
          initial={{ opacity: 0, scale: 0.6 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="px-5 text-center font-heading text-4xl font-extrabold uppercase tracking-[0.15em] text-gold sm:text-5xl md:text-6xl"
          data-testid="thefrent-on-purpose"
        >
          THEFRENT ON PURPOSE
        </motion.p>
      </section>

      {/* Leadership */}
      <section className="border-y border-border bg-secondary/50">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-28">
          <Reveal><Overline>Leadership</Overline></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              The people behind the mission.
            </h2>
          </Reveal>
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-3">
            {LEADERSHIP.map((p) => (
              <StaggerItem key={p.name}>
                <Link to="/admin?fresh=1" data-testid={`director-card-${p.name.split(" ")[0].toLowerCase()}`} aria-label={p.name} className="block h-full">
                  <div className="flair-card h-full rounded-2xl border border-border bg-card p-8">
                    <div className="flair-icon grid h-16 w-16 place-items-center rounded-full bg-navy-deep font-heading text-xl font-bold text-gold">
                      {p.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                    </div>
                    <h3 className="mt-6 font-heading text-lg font-semibold">{p.name}</h3>
                    <span className="mt-3 inline-flex rounded-full bg-gold px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide text-navy-deep shadow-sm" data-testid="director-title-pill">
                      {p.role}
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Legal / registration */}
      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-28">
        <div className="rounded-2xl border border-border bg-card p-8 md:p-12">
          <Overline>Official Registration</Overline>
          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div className="space-y-4 text-sm">
              <Row k="Legal Name" v={LEGAL.name} />
              <Row k="Organization Type" v="Nonprofit organization" />
              <Row k="Incorporated In" v={LEGAL.state} />
            </div>
            <div className="space-y-4 text-sm">
              <Row k="Principal Office" v={LEGAL.principalOffice} />
              <Row k="Registered Office" v={LEGAL.registeredOffice} />
              <Row k="Contact" v={LEGAL.email} />
            </div>
          </div>
          <p className="mt-8 text-xs leading-relaxed text-muted-foreground">
            Organized to serve charitable, educational, and community purposes. Thefrent Foundation
            is committed to transparent, responsible stewardship of every contribution.
          </p>
        </div>
        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="font-heading text-2xl font-semibold">Ready to make a difference?</h3>
            <Link
              to="/get-involved"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5"
              data-testid="about-cta"
            >
              Get Involved <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border pb-3">
      <span className="overline text-muted-foreground">{k}</span>
      <span className="font-medium text-foreground">{v}</span>
    </div>
  );
}
